import React, { useState } from 'react';
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconBrain, IconChevronDown } from '@tabler/icons-react';
import { ModelSelectionModal } from './ModelSelectionModal';
import { getModelIcons } from '../../lib/model-ui-utils';
import type { ModelSelectorProps } from '../../types/chat';

export const ModelSelector = React.memo<ModelSelectorProps>(({
  models,
  selectedModel,
  onModelSelect,
  isLoading = false,
  isLoggedIn = false,
  isDesktop,
}) => {
  const [modalOpened, setModalOpened] = useState(false);

  const selectedModelInfo = models.find(m => m.id === selectedModel);

  if (isLoading) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
        <Text size="xs" c="dimmed">Loading models...</Text>
      </Group>
    );
  }

  const tooltipLabel = selectedModelInfo 
    ? `${selectedModelInfo.name}: ${selectedModelInfo.description}`
    : 'Select a model';

  return (
    <>
      <Tooltip
        label={tooltipLabel}
        position="bottom"
        withArrow
        multiline={!isDesktop}
        maw={isDesktop ? undefined : 250}
      >
        {isDesktop !== false ? (
          <Button
            variant="subtle"
            leftSection={<IconBrain size={14} />}
            rightSection={<IconChevronDown size={14} />}
            onClick={() => setModalOpened(true)}
            size="compact"
            style={{ minWidth: 140, backgroundColor: 'transparent' }}
          >
            <Group gap="xs" justify="space-between" w="100%">
              <Text size="xs" truncate style={{ flex: 1 }}>
                {selectedModelInfo ? selectedModelInfo.name : 'Select Model'}
              </Text>
              {selectedModelInfo && (
                <Group gap={4}>
                  {getModelIcons(selectedModelInfo)}
                </Group>
              )}
            </Group>
          </Button>
        ) : (
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => setModalOpened(true)}
            aria-label="Select Model"
          >
            <IconBrain size={14} />
          </ActionIcon>
        )}
      </Tooltip>

      <ModelSelectionModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        models={models}
        selectedModel={selectedModel}
        onModelSelect={onModelSelect}
        isLoggedIn={isLoggedIn}
      />
    </>
  );
});

ModelSelector.displayName = 'ModelSelector';